// Final build fix - v1.0.1
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Section from '../components/Section';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import './Home.css';

// Simple Carousel Component
const EventCarousel = () => {
    const [slides, setSlides] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // compare by date only

        const anniversarySlide = {
            id: 'anniversary',
            title: "Anniversary Celebration",
            date: "Special Event",
            image: "/Images/aNNIVESSARY CELBRATION HERO.png",
            desc: "Join us as we celebrate and give thanks for God's faithfulness over the years. All are welcome!"
        };

        const fetchAnnouncements = async () => {
            try {
                const { data, error } = await supabase
                    .from('announcements')
                    .select('*')
                    .order('createdAt', { ascending: false });

                if (error) throw error;

                if (data && data.length > 0) {
                    const formattedData = data
                        .filter(item => {
                            // If a specific event date is stored, remove past events
                            if (item.eventDate) {
                                const eventDate = new Date(item.eventDate);
                                eventDate.setHours(0, 0, 0, 0);
                                return eventDate >= today;
                            }
                            // No event date set — keep it (it's a general announcement)
                            return true;
                        })
                        .map(item => ({
                            id: item.id,
                            title: item.title,
                            date: item.eventDate
                                ? new Date(item.eventDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                                : new Date(item.createdAt).toLocaleDateString(),
                            image: item.imageUrl || "/Images/worship.jpeg",
                            desc: item.content
                        }));
                    setSlides([anniversarySlide, ...formattedData]);
                } else {
                    setSlides([anniversarySlide]);
                }
            } catch (error) {
                console.error("Error fetching announcements:", error);
                setSlides([anniversarySlide]);
            }
        };
        fetchAnnouncements();
    }, []);

    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => slides.length ? (prev + 1) % slides.length : 0);
    }, [slides.length]);

    const prevSlide = useCallback(() => {
        setCurrentSlide((prev) => slides.length ? (prev - 1 + slides.length) % slides.length : 0);
    }, [slides.length]);

    // Auto-advance
    useEffect(() => {
        if (slides.length <= 1) return;
        const interval = setInterval(nextSlide, 5000);
        return () => clearInterval(interval);
    }, [nextSlide, slides.length]);

    if (slides.length === 0) return null;

    const hasImage = slides[currentSlide].image && !slides[currentSlide].image.includes('worship.jpeg') && !slides[currentSlide].image.includes('worship.jpg');

    return (
        <div className="carousel-container">
            <div className={`carousel-slide ${!hasImage ? 'no-image' : ''}`}>
                <div className="carousel-content-wrapper">
                    <div className="carousel-text-content">
                        <span className="event-tag">Upcoming Event</span>
                        <h2>{slides[currentSlide].title}</h2>
                        <p className="event-date">{slides[currentSlide].date}</p>
                        <p>{slides[currentSlide].desc}</p>
                    </div>
                    {hasImage && (
                        <div className="carousel-image-content animate-fade-in-scale">
                            <img src={slides[currentSlide].image} alt={slides[currentSlide].title} />
                        </div>
                    )}
                </div>
            </div>
            {slides.length > 1 && (
                <>
                    <button className="carousel-btn prev" onClick={prevSlide}><ChevronLeft /></button>
                    <button className="carousel-btn next" onClick={nextSlide}><ChevronRight /></button>
                    <div className="carousel-dots">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                className={`carousel-dot ${i === currentSlide ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(i)}
                                aria-label={`Go to slide ${i + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};


const ServiceCol = ({ day, time, title, desc }) => (
    <div className="service-time-row">
        <div className="service-day">{day}</div>
        <div className="service-details">
            <h4>{title}</h4>
            <div className="time-badge"><Clock size={14} /> {time}</div>
            {desc && <p>{desc}</p>}
        </div>
    </div>
);

const Home = () => {
    return (
        <Layout>
            <SEO title="Home" />
            <Hero />

            {/* Weekly Schedule Section */}
            <Section className="services-section">
                <div className="container">
                    <div className="text-center mb-50">
                        <h2 className="section-title">Weekly Gatherings</h2>
                        <p className="section-subtitle">Join us throughout the week to grow and fellowship.</p>
                    </div>

                    <div className="grid-2">
                        <div className="schedule-card sunday-card hover-lift animate-fade-in-up delay-100">
                            <h3>Sunday Service</h3>
                            <ServiceCol day="07:30 AM" title="METP Training" time="07:30 AM" />
                            <ServiceCol day="08:00 AM" title="Morning Prayer" time="08:00 AM" />
                            <ServiceCol day="08:30 AM" title="Bible Study" time="08:30 AM" />
                            <ServiceCol day="09:30 AM" title="Main Service" time="09:30 AM - 11:30 AM" desc="Worship, Word, and Ministry" />
                        </div>

                        <div className="schedule-card weekday-card hover-lift animate-fade-in-up delay-200">
                            <h3>Mid-Week</h3>
                            <ServiceCol day="Tuesday" title="Prayer Meeting" time="5:30 PM - 7:00 PM" />
                            <ServiceCol day="Wednesday" title="METP" time="5:30 PM - 6:30 PM" />
                            <ServiceCol day="Thursday" title="Ladies Meeting (China Chemadzimai)" time="9:00 AM - 12:00 PM" />
                            <ServiceCol day="Thursday" title="Homecells" time="Evening" desc="Fellowship in homes near you" />
                        </div>
                    </div>
                </div>
            </Section>

            {/* Impact / Stats Section */}
            <section className="impact-section">
                <div className="container">
                    <div className="impact-grid">
                        <div className="impact-item animate-fade-in-up delay-100">
                            <span className="impact-number">19+</span>
                            <span className="impact-label">Years of Ministry</span>
                        </div>
                        <div className="impact-item animate-fade-in-up delay-200">
                            <span className="impact-number">500+</span>
                            <span className="impact-label">Church Family Members</span>
                        </div>
                        <div className="impact-item animate-fade-in-up delay-300">
                            <span className="impact-number">4+</span>
                            <span className="impact-label">Ministries</span>
                        </div>
                        <div className="impact-item animate-fade-in-up delay-300">
                            <span className="impact-number">1</span>
                            <span className="impact-label">Community</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Events Carousel */}
            <section className="events-section">
                <EventCarousel />
            </section>

            {/* Photo Gallery Collage Section */}
            <section className="gallery-section bg-gray">
                <div className="container">
                    <div className="text-center mb-50">
                        <h2 className="section-title">Life at PAOZ HCC</h2>
                        <p className="section-subtitle">A glimpse into our vibrant community, worship, and fellowship.</p>
                    </div>
                    <div className="gallery-grid-rich">
                        {/* Row 1 */}
                        <div className="gallery-item gi-wide hover-lift">
                            <img src="/Images/Family Fun Day/WhatsApp Image 2026-06-15 at 2.01.56 PM.jpeg" alt="Family Fun Day" />
                            <div className="gallery-overlay"><h3>Family Fun Day</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Ladies/WhatsApp Image 2026-06-15 at 2.02.03 PM (2).jpeg" alt="Ladies Fellowship" />
                            <div className="gallery-overlay"><h3>Ladies Fellowship</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Youth Ministry/teens.jpg" alt="Youth Ministry" />
                            <div className="gallery-overlay"><h3>Youth Ministry</h3></div>
                        </div>

                        {/* Row 2 */}
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Family Fun Day/WhatsApp Image 2026-06-15 at 2.01.58 PM (1).jpeg" alt="Family Fun Day" />
                            <div className="gallery-overlay"><h3>Family Fun Day</h3></div>
                        </div>
                        <div className="gallery-item gi-wide hover-lift">
                            <img src="/Images/Church Service/WhatsApp Image 2026-01-31 at 2.14.10 PM (1).jpeg" alt="Sunday Service" />
                            <div className="gallery-overlay"><h3>Sunday Service</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Couples/WhatsApp Image 2026-06-15 at 2.01.59 PM (2).jpeg" alt="Couples" />
                            <div className="gallery-overlay"><h3>Couples</h3></div>
                        </div>

                        {/* Row 3 */}
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Ladies/WhatsApp Image 2026-06-15 at 2.02.03 PM (1).jpeg" alt="Ladies" />
                            <div className="gallery-overlay"><h3>Ladies</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Family Fun Day/WhatsApp Image 2026-06-15 at 2.02.06 PM.jpeg" alt="Family Fun Day" />
                            <div className="gallery-overlay"><h3>Family Fun Day</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Baptism/baptism.jpeg" alt="Water Baptism" />
                            <div className="gallery-overlay"><h3>Water Baptism</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Evangelism/WhatsApp Image 2026-06-15 at 2.01.35 PM.jpeg" alt="Evangelism" />
                            <div className="gallery-overlay"><h3>Evangelism</h3></div>
                        </div>

                        {/* Row 4 */}
                        <div className="gallery-item gi-wide hover-lift">
                            <img src="/Images/Family Fun Day/WhatsApp Image 2026-06-15 at 2.02.07 PM.jpeg" alt="Family Fun Day" />
                            <div className="gallery-overlay"><h3>Family Fun Day</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Baby Dedication/WhatsApp Image 2026-06-15 at 2.02.04 PM (1).jpeg" alt="Baby Dedication" />
                            <div className="gallery-overlay"><h3>Baby Dedication</h3></div>
                        </div>
                        <div className="gallery-item hover-lift">
                            <img src="/Images/Ladies/WhatsApp Image 2026-06-15 at 2.02.03 PM (3).jpeg" alt="Ladies" />
                            <div className="gallery-overlay"><h3>Ladies</h3></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cinematic Split-Screen "New Here" Section */}
            <section className="new-here-section">
                <div className="new-here-grid">
                    <div className="new-here-image-container animate-fade-in-scale">
                        <img src="/Images/Church Service/WhatsApp Image 2026-01-31 at 2.14.10 PM (1).jpeg" alt="PAOZ HCC Congregation" />
                    </div>
                    <div className="new-here-content-container">
                        <div className="new-here-content animate-fade-in-up">
                            <h2 className="new-here-title">You Belong Here</h2>
                            <p className="new-here-subtitle">
                                Whether you're visiting for the first time or looking for a permanent church family,
                                there is a place for you at PAOZ Hope Community Church. We can't wait to welcome you home.
                            </p>
                            <div className="new-here-cta-wrapper">
                                <a href="/contact" className="btn btn-primary new-here-btn">Plan Your Visit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Home;
